import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/Button';
import SafeIcon from '../src/common/SafeIcon';

const { FiMenu, FiX, FiArrowRight } = FiIcons;

interface NavbarProps {
  onLoginClick: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({ onLoginClick }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Curriculum', href: '#curriculum' },
    { name: 'Enroll', href: '#pricing' },
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled || isOpen ? 'bg-white shadow-md py-3' : 'bg-white/80 backdrop-blur-md py-4'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        <div className="font-serif text-xl font-bold text-slate-900 tracking-tight flex-shrink-0">
          Digital Assets Literacy
        </div>

        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a key={link.name} href={link.href} className="text-sm font-medium text-slate-600 hover:text-brand-900 transition-colors">
              {link.name}
            </a>
          ))}
          <div className="h-4 w-px bg-slate-200 mx-2" />
          <div className="flex items-center gap-4">
            <button onClick={onLoginClick} className="text-sm font-medium text-slate-900 hover:text-brand-700 transition-colors">
              Log In
            </button>
            <Button size="sm">Start Learning</Button>
          </div>
        </div>

        <div className="md:hidden flex items-center gap-4">
          <button onClick={() => setIsOpen(!isOpen)} className="p-2 text-slate-600 hover:text-slate-900 transition-colors">
            <SafeIcon icon={isOpen ? FiX : FiMenu} className="w-6 h-6" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="md:hidden bg-white border-t border-slate-100 overflow-hidden">
            <div className="px-4 py-8 space-y-6">
              <div className="flex flex-col gap-4">
                {navLinks.map((link) => (
                  <a key={link.name} href={link.href} onClick={() => setIsOpen(false)} className="text-lg font-semibold text-slate-900 hover:text-brand-600 transition-colors flex items-center justify-between group">
                    {link.name}
                    <SafeIcon icon={FiArrowRight} className="w-4 h-4 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
                  </a>
                ))}
              </div>
              <div className="pt-6 border-t border-slate-100 flex flex-col gap-4">
                <Button fullWidth variant="outline" onClick={() => { setIsOpen(false); onLoginClick(); }}>
                  Log In
                </Button>
                <Button fullWidth onClick={() => setIsOpen(false)}>
                  Start Learning
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};